import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';
import { ZagIcon } from '../icon/icon';

export type RatingDisplayType = 'stars' | 'slider' | 'percentage';

@Component({
  selector: 'zag-rating-display',
  imports: [ZagIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.zag-rating-display]': 'true',
    '[attr.aria-label]': 'ariaLabel()',
  },
  template: `
    @switch (displayType()) {
      @case ('stars') {
        <span class="zag-rating-display__stars">
          @for (star of stars(); track star.index) {
            <span
              zagIcon="{{ star.filled ? 'star' : 'star_border' }}"
              class="zag-rating-display__star"
              [class.zag-rating-display__star--filled]="star.filled"
              [class.zag-rating-display__star--half]="star.half"
            ></span>
          }
        </span>
      }
      @case ('slider') {
        <div class="zag-rating-display__bar">
          <div class="zag-rating-display__bar-fill" [style.width.%]="percentage()"></div>
        </div>
      }
      @case ('percentage') {
        <span class="zag-rating-display__percentage">{{ percentage() }}%</span>
      }
    }
  `,
  styles: [
    `
      .zag-rating-display {
        display: inline-flex;
        align-items: center;
      }

      .zag-rating-display__stars {
        display: inline-flex;
        gap: 2px;
      }

      .zag-rating-display__star {
        font-size: 18px;
        color: var(--text-color-tertiary);
        line-height: 1;
      }

      .zag-rating-display__star--filled {
        color: var(--color-warning, #f59e0b);
      }

      .zag-rating-display__bar {
        width: 100%;
        min-width: 60px;
        height: 6px;
        background-color: var(--surface-color-hover);
        border-radius: var(--border-radius-xs);
        overflow: hidden;
      }

      .zag-rating-display__bar-fill {
        height: 100%;
        background-color: var(--color-primary);
        border-radius: var(--border-radius-xs);
        transition: width var(--transition-duration-fast) var(--transition-timing);
      }

      .zag-rating-display__percentage {
        font-size: var(--font-size-body-desktop);
        font-weight: 500;
        font-variant-numeric: tabular-nums;
        color: var(--text-color-primary);
      }
    `,
  ],
})
export class RatingDisplay {
  readonly value = input<number | null>(null);
  readonly displayType = input<RatingDisplayType>('stars');
  readonly scale = input(5);

  protected readonly percentage = computed(() => {
    const v = this.value();
    if (v === null) return 0;
    return Math.round(v * 100);
  });

  protected readonly stars = computed(() => {
    const count = this.scale();
    const v = this.value() ?? 0;
    const filledCount = v * count;

    return Array.from({ length: count }, (_, i) => ({
      index: i,
      filled: i < Math.floor(filledCount),
      half: i === Math.floor(filledCount) && filledCount % 1 >= 0.5,
    }));
  });

  protected readonly ariaLabel = computed(() => {
    const v = this.value();
    if (v === null) return 'No rating';

    switch (this.displayType()) {
      case 'stars': {
        const starValue = Math.round(v * this.scale() * 10) / 10;
        return `${starValue} out of ${this.scale()} stars`;
      }
      case 'percentage':
      case 'slider':
        return `${this.percentage()}%`;
    }
  });
}
